import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import textHelper from '@utils/textHelper';
import { getImage } from '@utils/imageHelper';
import {
  BundleAddOn,
  getBundleAddCtaLabel,
  getBundleLineItems,
  getPlanTagDimensions,
  getPlanTypeTagImage,
  getTopologyTypeLabel
} from '@cont/Checkout/bundleAddOnsHelper';

const Image = styled.img`
  user-drag: none;
  user-select: none;
`;

const CardWrapper = styled.div<{ $isChecked: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
  border: ${Spacings.SPACING_1} solid
    ${({ $isChecked }) =>
      $isChecked ? 'var(--color-i-blue)' : 'var(--color-divider)'};
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-i-white);
  padding-top: ${Spacings.SPACING_3B};
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacings.SPACING_3B};
  padding: 0 ${Spacings.SPACING_3B};
`;

const ComboTitle = styled(Typography)`
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_18};
  color: var(--color-text-primary);
`;

const AddCta = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${Spacings.SPACING_8B};
  padding: 0 ${Spacings.SPACING_5B};
  border-radius: ${Spacings.SPACING_1B};
  border: ${Spacings.SPACING_1} solid
    ${({ $disabled }) =>
      $disabled ? 'var(--color-i-divider)' : 'var(--color-i-blue)'};
  background-color: var(--color-i-white);
  color: ${({ $disabled }) =>
    $disabled ? 'var(--color-i-gray-13)' : 'var(--color-i-blue)'};
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  line-height: ${Spacings.SPACING_6B};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const ComboBodyWrap = styled.div`
  padding: 0 ${Spacings.SPACING_3B};
`;

const ComboBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  background-color: var(--color-i-blue-11);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_1B};
`;

const TopologyRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_2B};
  background-color: var(--color-i-blue-11);
  border-radius: ${Spacings.SPACING_2B};
`;

const TopologyLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacings.SPACING_2B};
  min-width: 0;
`;

const TopologyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2};
  min-width: 0;
`;

const TopologyRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${Spacings.SPACING_2};
  flex-shrink: 0;
`;

const StrikeThrough = styled(Typography)`
  color: var(--color-text-secondary);
  text-decoration: line-through;
`;

const SeparatorLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
  width: 100%;
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: ${Spacings.SPACING_1};
    background-color: var(--color-i-divider);
  }
`;

const SeparatorIcon = styled.div`
  width: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
  border-radius: 50%;
  background-color: var(--color-i-white);
  border: ${Spacings.SPACING_1} solid var(--color-i-divider);
  background-image: url('${getImage('bundles/plus.png')}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 50%;
  flex-shrink: 0;
`;

interface MultiGoalComboAddonCardProps {
  bundle: BundleAddOn;
  index: number;
  isChecked?: boolean;
  disabled?: boolean;
  durationText?: string;
  onClick?: () => void;
}

const MultiGoalComboAddonCard = ({
  bundle,
  index,
  isChecked = false,
  disabled = false,
  durationText = '',
  onClick = () => {}
}: MultiGoalComboAddonCardProps) => {
  const currency = '₹';
  const ctaLabel = getBundleAddCtaLabel(bundle, currency);
  const topologyItems = getBundleLineItems(bundle);
  const resolvedDurationText =
    (bundle.subscription?.display_duration as string | undefined) ||
    durationText;

  return (
    <CardWrapper $isChecked={isChecked}>
      <CardHeader>
        <ComboTitle variant="h5">Combo {index + 1}</ComboTitle>
        <AddCta
          type="button"
          $disabled={disabled}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onClick();
          }}
        >
          {ctaLabel}
        </AddCta>
      </CardHeader>
      <ComboBodyWrap>
        <ComboBody>
          {topologyItems.map((item, subIndex) => {
            const showOriginalPrice =
              item.originalPrice !== undefined &&
              Math.round(item.originalPrice) > Math.round(item.price);
            return (
              <div key={item.key}>
                <TopologyRow>
                  <TopologyLeft>
                    <Image
                      width={getPlanTagDimensions(true).width}
                      height={getPlanTagDimensions(true).height}
                      src={getImage(getPlanTypeTagImage(item.type, true))}
                      alt="type"
                    />
                    <TopologyInfo>
                      <Typography variant="p2">
                        {item.name} · {getTopologyTypeLabel(item.type)}
                      </Typography>
                      {resolvedDurationText && (
                        <Typography
                          variant="p3"
                          color="var(--color-text-secondary)"
                        >
                          {resolvedDurationText}
                        </Typography>
                      )}
                    </TopologyInfo>
                  </TopologyLeft>
                  <TopologyRight>
                    <Typography variant="p2">
                      {currency}
                      {textHelper.commas(Math.round(item.price))}
                    </Typography>
                    {showOriginalPrice && (
                      <StrikeThrough variant="p3">
                        {currency}
                        {textHelper.commas(
                          Math.round(item.originalPrice as number)
                        )}
                      </StrikeThrough>
                    )}
                  </TopologyRight>
                </TopologyRow>
                {subIndex < topologyItems.length - 1 && (
                  <SeparatorLine>
                    <SeparatorIcon />
                  </SeparatorLine>
                )}
              </div>
            );
          })}
        </ComboBody>
      </ComboBodyWrap>
    </CardWrapper>
  );
};

export default MultiGoalComboAddonCard;
